import { useState } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';
import axios from 'axios';
import MarkdownMessage from './MarkdownMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROOTY_IMG = 'https://customer-assets.emergentagent.com/job_5adcc4e8-1bb2-4cdf-8c44-5c6c1ccd2118/artifacts/1skt1b81_IMG_1754.png';

/**
 * Inline "Ask Rooty" button. Click → calls /api/ai/chat with the supplied
 * contextPrompt prefilled, displays Rooty's response inline.
 */
export default function AskRootyButton({ label = 'Ask Rooty', contextPrompt, dataTestId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');

  const ask = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    setResponse('');
    setOpen(true);
    try {
      const sess = await axios.post(`${API_URL}/ai/sessions`);
      const sid = sess.data.id;
      const res = await axios.post(`${API_URL}/ai/chat`, {
        session_id: sid,
        message: contextPrompt,
        model: 'gemini',
      });
      setResponse(res.data.response);
    } catch (e) {
      setError(e.response?.data?.detail || 'Rooty is unavailable right now.');
    } finally {
      setLoading(false);
    }
  };

  // (legacy fmt removed — now handled by <MarkdownMessage />)

  return (
    <>
      <button
        onClick={ask}
        disabled={loading}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white text-orange-600 border border-orange-200 hover:bg-orange-50 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
        data-testid={dataTestId || 'ask-rooty-btn'}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {label}
      </button>

      {open && (
        <div className="mt-4 rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-4 relative" data-testid="ask-rooty-response">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
            data-testid="ask-rooty-close"
          ><X className="w-4 h-4" /></button>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 ring-2 ring-orange-300 overflow-hidden">
              <img src={ROOTY_IMG} alt="Rooty" className="w-9 h-9 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-slate-900 mb-1">Rooty says</p>
              {loading && (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing your data...
                </p>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
              {response && (
                <MarkdownMessage className="text-slate-700 prose-sm">{response}</MarkdownMessage>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
